import { AxiosError } from 'axios';
import { ApiService } from '../../api/api-service';
import {
  Building,
  BuildingRequest,
  BuildingWithTrusses,
  BuildingWithoutTrusses,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutValidationResponse,
  CloneBuildingRequest,
  DashboardKpiRequest,
  DashboardKpiResponse,
  DashboardMonthlyRequest,
  DashboardMonthlyResponse,
  Order,
  OrderDueDateUpdateRequest,
  OrderFilters,
  OrderListingResponse,
  OrdersDueReportResponse,
  OrdersPastDueReportResponse,
  OrderSignRequest,
  PaginatedResponse,
  ProductsReportResponse,
  SortParams,
  TravelerCompletionRequest,
} from '../../types';
import { flattenOrderForAPI } from '../../utils/data-transformers';

const SERVICE_ROOT = '/order';

/**
 * Service for order-related API calls and business logic
 */

export class OrderService extends ApiService {
  async getOrders(
    page: number,
    pageSize: number,
    filters?: OrderFilters,
    sort?: SortParams
  ): Promise<PaginatedResponse<OrderListingResponse>> {
    return this.handleApiCall(async () => {
      return this.client.get(SERVICE_ROOT, {
        params: {
          page,
          pageSize,
          ...filters,
          ...sort,
        },
      });
    }, 'Error fetching orders');
  }

  /**
   * Get order by ID
   * @param id Order ID
   * @returns Promise resolving to the order
   */
  async getOrderById(id: string): Promise<Order> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/${id}`),
      `Error fetching order with ID ${id}`
    );
  }

  /**
   * Get all orders for a specific manufacturer
   * @param manufacturerId Manufacturer ID
   * @returns Promise resolving to all orders for the manufacturer
   */
  async getOrdersByManufacturerId(manufacturerId: string): Promise<OrderListingResponse[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/manufacturer/${manufacturerId}`),
      `Error fetching orders for manufacturer ${manufacturerId}`
    );
  }

  /**
   * Get all orders for a specific dealer
   * @param dealerId Dealer ID
   * @returns Promise resolving to all orders for the dealer
   */
  async getOrdersByDealerId(dealerId: string): Promise<OrderListingResponse[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/dealer/${dealerId}`),
      `Error fetching orders for dealer ${dealerId}`
    );
  }

  /**
   * Get all buildings for a specific order (without truss details)
   * @param orderId Order ID
   * @returns Promise resolving to all buildings for the order
   */
  async getBuildingsByOrderId(orderId: string): Promise<BuildingWithoutTrusses[]> {
    try {
      return await this.client.get<BuildingWithoutTrusses[]>(
        `${SERVICE_ROOT}/${orderId}/buildings`
      );
    } catch (error: unknown) {
      // If it's a 404 (not found), return empty array instead of showing error toast
      // This is expected when an order has no buildings yet
      if (error instanceof AxiosError && error.response?.status === 404) {
        return [];
      }
      // For other errors, use the standard error handling
      return this.handleApiCall(
        () => Promise.reject(error),
        `Error fetching buildings for order ${orderId}`
      );
    }
  }

  /**
   * Get a building by ID with full truss details
   * @param buildingId Building ID
   * @returns Promise resolving to the building with trusses
   */
  async getBuildingById(buildingId: string): Promise<BuildingWithTrusses> {
    return this.handleApiCall(
      () => this.client.get<BuildingWithTrusses>(`${SERVICE_ROOT}/buildings/${buildingId}`),
      `Error fetching building ${buildingId}`
    );
  }

  /**
   * Add a building to an order
   * @param orderId Order ID
   * @param building Building data
   * @returns Promise resolving to the created building
   */
  async addBuildingToOrder(orderId: number, building: BuildingRequest): Promise<Building> {
    return this.handleApiCall(
      () => this.client.post<Building>(`${SERVICE_ROOT}/${orderId}/buildings`, building),
      `Error adding building to order ${orderId}`
    );
  }

  /**
   * Update a building
   * @param buildingId Building ID
   * @param building Building data to update
   * @returns Promise resolving when update is complete
   */
  async updateBuilding(buildingId: number, building: BuildingRequest): Promise<void> {
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/buildings/${buildingId}`, building),
      `Error updating building ${buildingId}`
    );
  }

  /**
   * Delete a building
   * @param buildingId Building ID
   * @returns Promise resolving when deletion is complete
   */
  async deleteBuilding(buildingId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.delete(`${SERVICE_ROOT}/buildings/${buildingId}`),
      `Error deleting building ${buildingId}`
    );
  }

  /**
   * Clone a building
   * @param cloneRequest Clone request with source building ID and target order ID
   * @returns Promise resolving to the cloned building
   */
  async cloneBuilding(cloneRequest: CloneBuildingRequest): Promise<Building> {
    return this.handleApiCall(
      () => this.client.post<Building>(`${SERVICE_ROOT}/clone-building`, cloneRequest),
      `Error cloning building ${cloneRequest.sourceBuildingId}`
    );
  }

  /**
   * Get count of buildings for a specific order
   * @param orderId Order ID
   * @returns Promise resolving to the count of buildings
   */
  async getBuildingsCountByOrderId(orderId: string): Promise<number> {
    try {
      return await this.client.get<number>(`${SERVICE_ROOT}/${orderId}/buildings/count`);
    } catch (error: unknown) {
      // If it's a 404 (not found), return 0 instead of showing error toast
      // This is expected when an order has no buildings yet
      if (error instanceof AxiosError && error.response?.status === 404) {
        return 0;
      }
      // For other errors, use the standard error handling
      return this.handleApiCall(
        () => Promise.reject(error),
        `Error fetching building count for order ${orderId}`
      );
    }
  }

  /**
   * Create a new order
   * @param order Order data
   * @returns Promise resolving to the created order
   */
  async createOrder(order: Omit<Order, 'id'>): Promise<OrderListingResponse> {
    const payload = flattenOrderForAPI(order as unknown as Record<string, unknown>);
    return this.handleApiCall(
      () => this.client.post(SERVICE_ROOT, payload),
      'Error creating order'
    );
  }

  /**
   * Update an order
   * @param id Order ID
   * @param order Order data to update
   * @returns Promise resolving to the updated order
   */
  async updateOrder(id: number, order: Order): Promise<void> {
    const payload = flattenOrderForAPI(order as unknown as Record<string, unknown>);
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/${id}`, payload),
      `Error updating order with ID ${id}`
    );
  }

  async confirmTrussStateChange(orderId: number, trussId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/trusses/${trussId}/confirm-state-change`),
      `Error confirming state change for truss ${trussId}`
    );
  }

  async confirmAllTrussStateChanges(orderId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/confirm-all-state-changes`),
      `Error confirming all state changes for order ${orderId}`
    );
  }

  /**
   * Validate if PO number is unique for a dealer
   * @param dealerId Dealer ID
   * @param po PO number to validate
   * @returns Promise resolving to isUnique boolean
   */
  async validatePOUniqueness(dealerId: number, po: string): Promise<boolean> {
    const result = await this.handleApiCall<{ isUnique: boolean }>(
      () => this.client.get(`${SERVICE_ROOT}/validate-po-uniqueness`, { params: { dealerId, po } }),
      `Error validating PO uniqueness for dealer ${dealerId}`
    );
    return result.isUnique;
  }

  /**
   * Get monthly dashboard data with orders and statistics
   * @param request Dashboard request parameters
   * @returns Promise resolving to dashboard data
   */
  async getMonthlyDashboard(request: DashboardMonthlyRequest): Promise<DashboardMonthlyResponse> {
    return this.handleApiCall(() => {
      // Build query string manually to handle array serialization correctly for ASP.NET Core
      const queryParams: string[] = [];
      queryParams.push(`year=${request.year}`);
      queryParams.push(`month=${request.month}`);

      if (request.manufacturerId) {
        queryParams.push(`manufacturerId=${request.manufacturerId}`);
      }

      // ASP.NET Core expects repeated parameters for arrays: statusFilters=3&statusFilters=4
      if (request.statusFilters && request.statusFilters.length > 0) {
        request.statusFilters.forEach(status => {
          queryParams.push(`statusFilters=${status}`);
        });
      }

      if (request.pastDueOnly) {
        queryParams.push(`pastDueOnly=${request.pastDueOnly}`);
      }

      if (request.dueWithin7Days) {
        queryParams.push(`dueWithin7Days=${request.dueWithin7Days}`);
      }

      queryParams.push(`showWeekly=${request.showWeekly}`);
      queryParams.push(`showPONumbers=${request.showPONumbers}`);

      if (request.includeKpi !== undefined) {
        queryParams.push(`includeKpi=${request.includeKpi}`);
      }

      if (request.kpiViewMode) {
        queryParams.push(`kpiViewMode=${request.kpiViewMode}`);
      }

      if (request.includePartialPeriods !== undefined) {
        queryParams.push(`includePartialPeriods=${request.includePartialPeriods}`);
      }

      // Prefer date range over weekNumber for calendar week support
      if (request.startDate && request.endDate) {
        queryParams.push(`startDate=${request.startDate}`);
        queryParams.push(`endDate=${request.endDate}`);
      } else if (request.weekNumber) {
        // Fallback to weekNumber for backward compatibility
        queryParams.push(`weekNumber=${request.weekNumber}`);
      }

      const queryString = queryParams.join('&');
      return this.client.get<DashboardMonthlyResponse>(
        `${SERVICE_ROOT}/dashboard/monthly?${queryString}`
      );
    }, 'Error fetching dashboard data');
  }

  /**
   * Get dashboard KPI metrics for production
   * @param request KPI request parameters
   * @returns Promise resolving to KPI metrics
   */
  async getDashboardKpi(request: DashboardKpiRequest): Promise<DashboardKpiResponse> {
    return this.handleApiCall(() => {
      const queryParams: string[] = [];

      if (request.manufacturerId) {
        queryParams.push(`manufacturerId=${request.manufacturerId}`);
      }

      if (request.includePartialPeriods !== undefined) {
        queryParams.push(`includePartialPeriods=${request.includePartialPeriods}`);
      }

      if (request.viewMode) {
        queryParams.push(`viewMode=${request.viewMode}`);
      }

      if (request.year) {
        queryParams.push(`year=${request.year}`);
      }

      if (request.month) {
        queryParams.push(`month=${request.month}`);
      }

      if (request.startDate) {
        queryParams.push(`startDate=${request.startDate}`);
      }

      if (request.endDate) {
        queryParams.push(`endDate=${request.endDate}`);
      }

      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      return this.client.get<DashboardKpiResponse>(`${SERVICE_ROOT}/dashboard/kpi${queryString}`);
    }, 'Error fetching KPI data');
  }

  /**
   * Update an order's due date (for calendar drag-and-drop)
   * @param orderId Order ID
   * @param request Update request with new due date
   * @returns Promise resolving when update is complete
   */
  async updateOrderDueDate(orderId: number, request: OrderDueDateUpdateRequest): Promise<void> {
    return this.handleApiCall(
      () => this.client.patch(`${SERVICE_ROOT}/${orderId}/due-date`, request),
      `Error updating due date for order ${orderId}`
    );
  }

  /**
   * Place an order on hold from ReadyForPickup status
   * @param orderId Order ID
   * @returns Promise resolving when operation is complete
   */
  async placeOrderOnHold(orderId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/hold`),
      `Error placing order ${orderId} on hold`
    );
  }

  /**
   * Mark an order as shipped (terminal status)
   * @param orderId Order ID
   * @returns Promise resolving when operation is complete
   */
  async shipOrder(orderId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/ship`),
      `Error shipping order ${orderId}`
    );
  }

  async releaseForPickup(orderId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/release-for-pickup`),
      `Error releasing order ${orderId} for pickup`
    );
  }

  async releaseForShipment(orderId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/release-for-shipment`),
      `Error releasing order ${orderId} for shipment`
    );
  }

  async releaseOrderFromHold(orderId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/release-hold`),
      `Error releasing order ${orderId} from hold`
    );
  }

  async releaseToProduction(orderId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/${orderId}/release-to-production`),
      `Error releasing order ${orderId} to production`
    );
  }

  async signOrder(orderId: number, request: OrderSignRequest): Promise<void> {
    return this.handleApiCall(
      () => this.client.patch(`${SERVICE_ROOT}/${orderId}/sign`, request),
      `Error signing order ${orderId}`
    );
  }

  async updateTravelerCompletion(
    orderId: number,
    request: TravelerCompletionRequest
  ): Promise<void> {
    return this.handleApiCall(
      () => this.client.patch(`${SERVICE_ROOT}/${orderId}/traveler-completion`, request),
      `Error updating traveler completion for order ${orderId}`
    );
  }

  async validateCheckout(orderId: number): Promise<CheckoutValidationResponse> {
    return this.handleApiCall(
      () =>
        this.client.post<CheckoutValidationResponse>(
          `${SERVICE_ROOT}/${orderId}/validate-checkout`
        ),
      `Error validating checkout for order ${orderId}`
    );
  }

  async checkoutOrder(orderId: number, request?: CheckoutRequest): Promise<CheckoutResponse> {
    return this.handleApiCall(
      () =>
        this.client.post<CheckoutResponse>(`${SERVICE_ROOT}/${orderId}/checkout`, request ?? {}),
      `Error checking out order ${orderId}`
    );
  }

  async updateOrderStatus(orderId: number, newStatus: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.patch(`${SERVICE_ROOT}/${orderId}/status`, { newStatus }),
      `Error updating status for order ${orderId}`
    );
  }

  /**
   * Private helper method to build KPI query string
   * @param params Query parameters for KPI endpoint
   * @returns Formatted query string
   */
  private buildKpiQuery(params: {
    viewMode: 'week' | 'month';
    startDate?: string;
    endDate?: string;
    year?: number;
    month?: number;
    manufacturerId?: number;
  }): string {
    const queryParams: string[] = [`viewMode=${params.viewMode}`];

    if (params.startDate) {
      queryParams.push(`startDate=${params.startDate}`);
    }
    if (params.endDate) {
      queryParams.push(`endDate=${params.endDate}`);
    }
    if (params.year !== undefined) {
      queryParams.push(`year=${params.year}`);
    }
    if (params.month !== undefined) {
      queryParams.push(`month=${params.month}`);
    }
    if (params.manufacturerId) {
      queryParams.push(`manufacturerId=${params.manufacturerId}`);
    }

    return queryParams.join('&');
  }

  /**
   * Get weekly feet per day report data
   * @param startDate Start date (Monday, format: YYYY-MM-DD)
   * @param endDate End date (Sunday, format: YYYY-MM-DD)
   * @param manufacturerId Optional manufacturer ID (for admin users)
   * @returns Promise resolving to KPI data with daily breakdown
   */
  async getWeeklyFeetPerDayReport(
    startDate: string,
    endDate: string,
    manufacturerId?: number
  ): Promise<DashboardKpiResponse> {
    const queryString = this.buildKpiQuery({
      viewMode: 'week',
      startDate,
      endDate,
      manufacturerId,
    });

    return this.handleApiCall(
      () => this.client.get<DashboardKpiResponse>(`${SERVICE_ROOT}/dashboard/kpi?${queryString}`),
      'Error fetching weekly feet per day report'
    );
  }

  /**
   * Get total feet per week report data
   * @param startDate Start date (Monday, format: YYYY-MM-DD)
   * @param endDate End date (Sunday, format: YYYY-MM-DD)
   * @param manufacturerId Optional manufacturer ID (for admin users)
   * @returns Promise resolving to KPI data with weekly totals
   */
  async getTotalFeetPerWeekReport(
    startDate: string,
    endDate: string,
    manufacturerId?: number
  ): Promise<DashboardKpiResponse> {
    const queryString = this.buildKpiQuery({
      viewMode: 'week',
      startDate,
      endDate,
      manufacturerId,
    });

    return this.handleApiCall(
      () => this.client.get<DashboardKpiResponse>(`${SERVICE_ROOT}/dashboard/kpi?${queryString}`),
      'Error fetching total feet per week report'
    );
  }

  /**
   * Get total feet per month report data
   * @param year Year (e.g., 2026)
   * @param month Month (1-12)
   * @param manufacturerId Optional manufacturer ID (for admin users)
   * @returns Promise resolving to KPI data with monthly totals
   */
  async getTotalFeetPerMonthReport(
    year: number,
    month: number,
    manufacturerId?: number
  ): Promise<DashboardKpiResponse> {
    const queryString = this.buildKpiQuery({
      viewMode: 'month',
      year,
      month,
      manufacturerId,
    });

    return this.handleApiCall(
      () => this.client.get<DashboardKpiResponse>(`${SERVICE_ROOT}/dashboard/kpi?${queryString}`),
      'Error fetching total feet per month report'
    );
  }

  async getOrdersDueReport(
    startDate: string,
    endDate: string,
    dealerId?: number,
    manufacturerId?: number
  ): Promise<OrdersDueReportResponse> {
    const queryParams: string[] = [`startDate=${startDate}`, `endDate=${endDate}`];
    if (dealerId !== undefined) {
      queryParams.push(`dealerId=${dealerId}`);
    }
    if (manufacturerId !== undefined) {
      queryParams.push(`manufacturerId=${manufacturerId}`);
    }
    const queryString = queryParams.join('&');

    return this.handleApiCall(
      () =>
        this.client.get<OrdersDueReportResponse>(
          `${SERVICE_ROOT}/report/orders-due?${queryString}`
        ),
      'Error fetching orders due report'
    );
  }

  async getOrdersPastDueReport(
    dealerId?: number,
    manufacturerId?: number
  ): Promise<OrdersPastDueReportResponse> {
    const queryParams: string[] = [];
    if (dealerId !== undefined) {
      queryParams.push(`dealerId=${dealerId}`);
    }
    if (manufacturerId !== undefined) {
      queryParams.push(`manufacturerId=${manufacturerId}`);
    }
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    return this.handleApiCall(
      () =>
        this.client.get<OrdersPastDueReportResponse>(
          `${SERVICE_ROOT}/report/orders-past-due${queryString}`
        ),
      'Error fetching orders past due report'
    );
  }

  async getProductsReport(
    manufacturerId?: number,
    trussType?: string,
    orderDateFrom?: string,
    orderDateTo?: string
  ): Promise<ProductsReportResponse> {
    const queryParams: string[] = [];
    if (manufacturerId !== undefined) {
      queryParams.push(`manufacturerId=${manufacturerId}`);
    }
    if (trussType) {
      queryParams.push(`trussType=${encodeURIComponent(trussType)}`);
    }
    if (orderDateFrom) {
      queryParams.push(`orderDateFrom=${orderDateFrom}`);
    }
    if (orderDateTo) {
      queryParams.push(`orderDateTo=${orderDateTo}`);
    }
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    return this.handleApiCall(
      () =>
        this.client.get<ProductsReportResponse>(`${SERVICE_ROOT}/report/products${queryString}`),
      'Error fetching products report'
    );
  }
}
